import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import { STATE_COLOR, GRAPH_BOUNDS, validValue, formatDateTime } from '../components/enums';
import TimerProgressBar from '../components/timer_progress_bar';
import CooldownButton from '../components/cooldown_button';
import SystemRealtimeGraph from '../components/realtime/realtime_system_graph';
import StringRealtimeGraph from '../components/realtime/realtime_string_graph';
import TemperatureRealtimeGraph from '../components/realtime/realtime_temp_graph';
import UnitsRealtimeGraph from '../components/realtime/realtime_units_graph';
import useLocationGraphConfig, { ComputeScaleExtremes } from '../components/hooks/useLocationGraphConfig';

// Material UI
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// Material UI Icons
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';


// Realtime Intervals Options - value is in seconds
const RealtimeIntervals = [
	{ value: 0, label: 'Auto: Off' },
	{ value: 15, label: '15 sec' },
	{ value: 30, label: '30 sec' },
	{ value: 60, label: '60 sec' },
	{ value: 90, label: '90 sec' },
	{ value: 120, label: '2 min' },
	{ value: 300, label: '5 min' },
	{ value: 600, label: '10 min' },
];

// Stale Data Threshold - number of days before data is considered stale
const STALE_DATA_THRESHOLD_DAYS = 7;

// Event notification bar component - moved outside to prevent remounting
function EventNotificationBar({ eventInfo, narrowScreen = false }) {
	if (!eventInfo) { return null; }

	const displayText = narrowScreen 
		? `${eventInfo.label}: ${formatDateTime(eventInfo.startDateTime)} (${eventInfo.duration})`
		: `${eventInfo.label}: ${eventInfo.duration}`;

	return (
		<Tooltip
			title={
				<div>
					<div>Started: {formatDateTime(eventInfo.startDateTime)}</div>
					<div>Duration: {eventInfo.duration}</div>
				</div>
			}
			arrow
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: narrowScreen ? 'center' : 'flex-start',
					...(narrowScreen 
						? { borderBottom: `2px solid ${eventInfo.color}` }
						: { border: `2px solid ${eventInfo.color}`, borderRadius: '4px' }
					),
					color: eventInfo.color,
					backgroundColor: `${eventInfo.color}15`,
					gap: 0.5,
					px: 1,
					py: 0.25,
					fontFamily: 'Saira,sans-serif',
					fontWeight: '600',
					cursor: 'default',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
					{eventInfo.icon}
				</Box>
				<span style={{ whiteSpace: 'nowrap' }}>
					{displayText}
				</span>
			</Box>
		</Tooltip>
	);
}

// Extract discharge/charge event information from realtime data with robust error handling
function GetDischargeChargeEventInfo(realtimeData) {
	// Validate input data structure
	if (!realtimeData) {
		return null;
	}

	const isInDischarge = Boolean(realtimeData?.system?.in_discharge);
	const isInCharge = Boolean(realtimeData?.system?.in_charge);
	const durationData = realtimeData?.discharge_time;

	// No active event or invalid data
	if ((!isInDischarge && !isInCharge) || !durationData?.event_start) { return null; }

	// Determine event type (discharge takes priority if both flags are set)
	const isDischarge = isInDischarge;

	// Build duration string from individual components
	const days = durationData.event_duration_days ?? 0;
	const hours = durationData.event_duration_hours ?? 0;
	const mins = durationData.event_duration_mins ?? 0;
	const secs = durationData.event_duration_secs ?? 0;
	
	// Build duration string, excluding zero days/hours
	const durationParts = [];
	if (days > 0) { durationParts.push(`${days}d`); }
	if (hours > 0) { durationParts.push(`${hours}h`); }
	durationParts.push(`${mins}m`);
	durationParts.push(`${secs}s`);
	const duration = durationParts.join(' ');
	
	return {
		isDischarge,
		startDateTime: durationData.event_start,
		duration: duration,
		color: isDischarge ? STATE_COLOR.CRITICAL : STATE_COLOR.WITHIN_LIMITS,
		icon: isDischarge ? <ArrowCircleDownOutlinedIcon /> : <ArrowCircleUpOutlinedIcon />,
		label: isDischarge ? 'In Discharge' : 'Charging',
	};
}

// Stale Data Blocker Component
function StaleDataBlocker({ staleDataState, onViewStaleData }) {
	
	const handleViewStaleData = () => {
		onViewStaleData();
	};
	
	const getBlockerContent = () => {
		// Reusable help text with link to support page
		const helpText = (
			<>
				Please check the system for connectivity, then{' '}
				<Link 
					href="https://www.btechinc.com/support/" 
					target="_blank" 
					rel="noopener noreferrer"
					sx={{ color: 'primary.main', textDecoration: 'underline' }}
				>
					contact support
				</Link>
				{' '}if problems persist.
			</>
		);
		
		const lastPollDate = staleDataState.lastPoll ? formatDateTime(staleDataState.lastPoll) : 'Unknown';

		// Determine message and button visibility based on stale data state
		let content = {
			title: "System Communication Issues",
			message: helpText,
			lastPollDate: lastPollDate,
			showViewButton: false,
		}

		if (staleDataState.lastPoll === null) {

			content.message = (
				<>
					This system has never successfully communicated with the server. {helpText}
				</>
			);

		} else if (staleDataState.hasNoData) {
			
			content.message = (
				<>
					This system hasn't communicated in over {STALE_DATA_THRESHOLD_DAYS} days, and no real-time data is available. {helpText}
				</>
			);

		} else {
			
			content.message = (
				<>
					This system hasn't communicated in over {STALE_DATA_THRESHOLD_DAYS} days, so the data being displayed may be outdated. {helpText}
				</>
			);
			content.showViewButton = true;

		}

		return content;
	};

	const content = getBlockerContent();

	if (!staleDataState.dialogOpen) {
		return null;
	}

	return (
		<>
			{/* Dark backdrop that covers the content area */}
			<Backdrop
				open={staleDataState.dialogOpen}
				sx={{
					position: 'absolute',
					zIndex: 1200,
					backgroundColor: 'rgba(0, 0, 0, 0.75)',
				}}
			/>
			{/* Alert card positioned in the center */}
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					zIndex: 1201,
					width: '90%',
					maxWidth: '600px',
				}}
			>
				<Card elevation={8}>
					<CardContent>
						<Typography variant="h6" sx={{ fontFamily: 'Saira, sans-serif', fontWeight: 600, mb: 2 }}>
							{content.title}
						</Typography>
						<Typography sx={{ fontFamily: 'Saira, sans-serif', mb: 2 }}>
							{content.message}
						</Typography>
						<Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(243, 39, 53, 0.1)', borderRadius: 1, border: '1px solid rgba(243, 39, 53, 0.3)' }}>
							<Typography sx={{ fontFamily: 'Saira, sans-serif', fontSize: '0.9rem', color: '#666' }}>
								Last Successful Poll:
							</Typography>
							<Typography sx={{ fontFamily: 'Saira, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#d32f2f', mt: 0.5 }}>
								{content.lastPollDate}
							</Typography>
						</Box>
					</CardContent>
					{content.showViewButton && (
						<CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
							<Button 
								onClick={handleViewStaleData} 
								variant="contained" 
								color="primary" 
								sx={{ fontFamily: 'Saira, sans-serif' }}
							>
								View Old Data
							</Button>
						</CardActions>
					)}
				</Card>
			</Box>
		</>
	);
}

// Unit Snapshot sub-page rendering (Location Name, Title, String Selection, Measurement Selection)
export default function Realtime() {
	const { locationNum } = useParams();
	const theme = useTheme();
	const isNarrowScreen = useMediaQuery(theme.breakpoints.down(1285));

	// Page State
	const [autoUpdate, setAutoUpdate] = useState({
		isAllowed: true,
		isEnabled: true,
		interval: 15,  // seconds, 0 = OFF
	});
	const [timerResetTrigger, setTimerResetTrigger] = useState(0);

	// Stale Data State
	const [staleDataState, setStaleDataState] = useState({
		isStale: false,           // Data is older than 1 week or null
		lastPoll: null,           // Date of last successful poll
		hasNoData: false,         // No data available - structure with null values
		dialogOpen: false,        // Dialog visibility
		viewingStaleData: false,  // User chose to view stale data
	});

	// Measurement Data and Currently Selected String
	const [realtimeData, setRealtimeData] = useState(null);
	const [selectedString, setSelectedString] = useState(0);
	const [initialDataFetched, setInitialDataFetched] = useState(false);
	
	// system and Strings Limits and Graph scales
	const [sysLimitsData, setSysLimitsData] = useState(null);
	const [sysGraphScalesData, setSysGraphScalesData] = useState(null);

	// Discharge Limits State
	const [sysDischargeLimitsData, setSysDischargeLimitsData] = useState(null);
	const [stringDischargeLimitsData, setStringDischargeLimitsData] = useState(null);
	const [unitDischargeLimitsData, setUnitDischargeLimitsData] = useState(null);

	// Track if discharge limits have been fetched to prevent duplicate calls
	const dischargeLimitsFetchedRef = useRef({
		system: false,
		string: false,
		unit: false,
	});

	// Track if initial stale data check has been performed
	const initialStaleCheckDoneRef = useRef(false);

	// Get location info, and unit limits and graph scales
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits_flat/unit`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales/unit`
	});
	const unitGraphScalesExtremes = useMemo(() => ComputeScaleExtremes(graphScalesData), [graphScalesData]);

	// Generic data fetching function
	const fetchData = useCallback((endpoint, setStateCallback, errorMessage, manageLoading = false) => {
		if (manageLoading) {
			setIsLoading(true);
		}

		return axiosInstance.get(endpoint)
			.then(response => {
				setStateCallback(response.data);
				return response.data;
			})
			.catch(error => {
				console.error(errorMessage, error);
			})
			.finally(() => {
				if (manageLoading) {
					setIsLoading(false);
				}
			});
	}, [setIsLoading]);

	// Validate if system data has actual measurements (not just null values)
	const hasValidSystemData = useCallback((data) => {
		if (!data) return false;
		return data.system && data.system.timestamp !== null;
	}, []);

	// Validate if strings data has actual measurements (not just null values)
	const hasValidStringsData = useCallback((data) => {
		if (!data || !data.strings || !Array.isArray(data.strings)) return false;
		return data.strings.some(str => str.timestamp !== null);
	}, []);

	// Validate if units data has actual measurements (not just null values)
	const hasValidUnitsData = useCallback((data) => {
		if (!data || !data.units || !Array.isArray(data.units)) return false;
		return data.units.some(unit => unit.timestamp !== null);
	}, []);

	// Validate if temperature sensor data has actual measurements (not just null values)
	const hasValidTempSensorData = useCallback((data) => {
		if (!data || !data.temperatures || !Array.isArray(data.temperatures)) return false;
		return data.temperatures.some(temp => temp.temperature !== null && temp.temperature !== undefined);
	}, []);

	// Validate if unit temperature data has actual measurements (not just null values)
	const hasValidUnitTempData = useCallback((data) => {
		if (!data || !data.units || !Array.isArray(data.units)) return false;
		return data.units.some(unit => unit.temperature !== null && unit.temperature !== undefined);
	}, []);

	// Get the latest realtime data
	const UpdateRealtimeData = useCallback(() => {
		setIsLoading(true);
		setTimerResetTrigger(prev => prev + 1); // Reset the timer

		axiosInstance.get(`/loc/${locationNum}/realtime/`)
			.then(response => {
				setRealtimeData(response.data);

				// determine if auto-update is enabled based on quickpoll setting
				const quickpollEnabled = response?.data?.quickpoll_enabled;
				if (quickpollEnabled !== undefined) {
					setAutoUpdate(prevState => ({
						...prevState,
						isAllowed: !quickpollEnabled,
						isEnabled: !quickpollEnabled ? prevState.isEnabled : false
					}));
				}
			})
			.catch(error => {
				console.error("Error fetching realtime measurement data:", error);
			})
			.finally(() => {
				setIsLoading(false);
				if (!initialDataFetched) {
					setInitialDataFetched(true);
				}
			});
	}, [locationNum]);

	// Get the system limits data
	const UpdateSysLimitsData = useCallback(() => {
		return fetchData(
			`/loc/${locationNum}/limits`, setSysLimitsData, "Error fetching system limits data: ", true
		);
	}, [locationNum, fetchData]);

	// Get the system graph scales data
	const UpdateSysGraphScalesData = useCallback(() => {
		return fetchData(
			`/loc/${locationNum}/graph_scales`, setSysGraphScalesData, "Error fetching system graph scales data: ", true
		);
	}, [locationNum, fetchData]);

	// Get the system discharge limits data
	const UpdateSysDischargeLimitsData = useCallback(() => {
		return fetchData(
			`/loc/${locationNum}/limits/discharge`, setSysDischargeLimitsData, "Error fetching system discharge limits data: "
		);
	}, [locationNum, fetchData]);

	// Get the string discharge limits data
	const UpdateStringDischargeLimitsData = useCallback(() => {
		return fetchData(
			`/loc/${locationNum}/limits/discharge/string`, setStringDischargeLimitsData, "Error fetching string discharge limits data: "
		);
	}, [locationNum, fetchData]);

	// Get the unit discharge limits data
	const UpdateUnitDischargeLimitsData = useCallback(() => {
		return fetchData(
			`/loc/${locationNum}/limits_flat/discharge/unit`, setUnitDischargeLimitsData, "Error fetching unit discharge limits data: "
		);
	}, [locationNum, fetchData]);

	// Initial data fetch
	useEffect(() => {
		UpdateRealtimeData();
	}, [UpdateRealtimeData]);

	useEffect(() => {
		UpdateSysLimitsData();
	}, [UpdateSysLimitsData]);

	useEffect(() => {
		UpdateSysGraphScalesData();
	}, [UpdateSysGraphScalesData]);

	// Helpers to format the page heading text
	const isRealtimeDataEmpty = !realtimeData || (Object.keys(realtimeData).length === 0);
	const measurementDateTime = isRealtimeDataEmpty ? null : formatDateTime(realtimeData?.system?.timestamp);
	const headingText = isLoading ? "Realtime Data Loading..." : (isRealtimeDataEmpty ? "No Realtime Data Available" : measurementDateTime);

	// Helper to determine if timer should be visible
	const isTimerVisible = autoUpdate.isEnabled && autoUpdate.isAllowed && autoUpdate.interval >= 15;

	// Extract event information from realtime data - memoized to prevent unnecessary recalculations
	const eventInfo = useMemo(() => GetDischargeChargeEventInfo(realtimeData), [realtimeData]);

	// Compute validation results for each data type - memoized to avoid recomputation
	const hasValidSystem = useMemo(() => hasValidSystemData(realtimeData), [realtimeData, hasValidSystemData]);
	const hasValidStrings = useMemo(() => hasValidStringsData(realtimeData), [realtimeData, hasValidStringsData]);
	const hasValidUnits = useMemo(() => hasValidUnitsData(realtimeData), [realtimeData, hasValidUnitsData]);
	const hasValidTempSensor = useMemo(() => hasValidTempSensorData(realtimeData), [realtimeData, hasValidTempSensorData]);
	const hasValidUnitTemp = useMemo(() => hasValidUnitTempData(realtimeData), [realtimeData, hasValidUnitTempData]);

	// Global validation - combines individual memoized results
	const hasValidRealtimeData = useMemo(() => {
		return hasValidSystem || hasValidStrings || hasValidUnits || hasValidTempSensor || hasValidUnitTemp;
	}, [hasValidSystem, hasValidStrings, hasValidUnits, hasValidTempSensor, hasValidUnitTemp]);

	// Check if data is stale based on last successful poll
	const CheckLastSuccessfulPoll = useCallback(() => {
		axiosInstance.get(`/loc/${locationNum}/settings/last_poll`)
			.then(response => {
				const lastPoll = response.data.last_successful_poll;
				const hasValidData = hasValidRealtimeData;
				
				// Check if lastPoll is null, which indicates no successful communication has ever occurred
				if (lastPoll === null) {
					setStaleDataState({
						isStale: true,
						lastPoll: null,
						hasNoData: true,
						dialogOpen: true,
						viewingStaleData: false,
					});
					// Disable auto-refresh when no data
					setAutoUpdate(prevState => ({
						...prevState,
						isAllowed: false,
						isEnabled: false,
					}));
					return;
				}

				// Check if data is older than the stale threshold
				const lastPollDate = new Date(lastPoll);
				const thresholdDate = new Date();
				thresholdDate.setDate(thresholdDate.getDate() - STALE_DATA_THRESHOLD_DAYS);

				const isStale = lastPollDate < thresholdDate;
				if (isStale) {
					setStaleDataState({
						isStale: true,
						lastPoll: lastPoll,
						hasNoData: !hasValidData,
						dialogOpen: true,
						viewingStaleData: false,
					});
					// Disable auto-refresh when stale
					setAutoUpdate(prevState => ({
						...prevState,
						isAllowed: false,
						isEnabled: false,
					}));
				} else {
					// Data is fresh, but we still need to check if measurements exist
					if (!hasValidData && realtimeData) {
						// Poll is recent but no measurements exist
						setStaleDataState({
							isStale: true,
							lastPoll: lastPoll,
							hasNoData: true,
							dialogOpen: true,
							viewingStaleData: false,
						});
						setAutoUpdate(prevState => ({
							...prevState,
							isAllowed: false,
							isEnabled: false,
						}));
					} else {
						setStaleDataState({
							isStale: false,
							lastPoll: lastPoll,
							hasNoData: false,
							dialogOpen: false,
							viewingStaleData: false,
						});
					}
				}
			})
			.catch(error => {
				console.error("Error fetching last successful poll:", error);
			});
	}, [locationNum, hasValidRealtimeData, realtimeData, setAutoUpdate, setStaleDataState]);

	// Check last successful poll after initial realtime data fetch completes
	useEffect(() => {
		if (initialDataFetched && !initialStaleCheckDoneRef.current) {
			CheckLastSuccessfulPoll();
			initialStaleCheckDoneRef.current = true;
		}
	}, [initialDataFetched, CheckLastSuccessfulPoll]);

	// Determine active limits based on discharge state - memoized to prevent unnecessary recalculations
	// Falls back to regular limits if discharge limits aren't loaded yet
	const activeSysLimits = useMemo(() => {
		if (!eventInfo?.isDischarge) return sysLimitsData;
		if (!sysDischargeLimitsData) return sysLimitsData; // Fallback while loading
		
		return {
			no_current_measurement: sysDischargeLimitsData.no_current_measurement,
			current_per_string: sysDischargeLimitsData.current_per_string,
			volt: {
				hi_crit: null,
				lo_crit: sysDischargeLimitsData.volt
			},
			current: {
				system: sysDischargeLimitsData.current.discharge,
				string: sysDischargeLimitsData.current.discharge
			},
			temp: sysDischargeLimitsData.temp
		};
	}, [eventInfo?.isDischarge, sysDischargeLimitsData, sysLimitsData]);

	const activeStringLimits = useMemo(() => {
		if (!eventInfo?.isDischarge) return sysLimitsData;
		if (!stringDischargeLimitsData) return sysLimitsData; // Fallback while loading
		
		return {
			no_current_measurement: stringDischargeLimitsData.no_current_measurement,
			current_per_string: stringDischargeLimitsData.current_per_string,
			volt: {
				hi_crit: null,
				lo_crit: stringDischargeLimitsData.volt
			},
			current: {
				system: stringDischargeLimitsData.current.discharge,
				string: stringDischargeLimitsData.current.discharge
			},
			temp: stringDischargeLimitsData.temp
		};
	}, [eventInfo?.isDischarge, stringDischargeLimitsData, sysLimitsData]);

	const activeUnitLimits = useMemo(() => {
		if (!eventInfo?.isDischarge) return limitData;
		if (!unitDischargeLimitsData) return limitData; // Fallback while loading
		
		return unitDischargeLimitsData.map(stringLimit => ({
			string_nbr: stringLimit.string_nbr,
			volt: {
				hi_crit: null,
				hi_maint: null,
				lo_maint: stringLimit.volt.lo_maint,
				lo_crit: stringLimit.volt.lo_crit
			},
			temp: stringLimit.temp
		}));
	}, [eventInfo?.isDischarge, unitDischargeLimitsData, limitData]);
	
	// Helper to determine active graph scales based on discharge state
	function calculateSysStrGraphScales(isInDischarge, graphScales) {
		// Determine default based on if the system is in discharge
		const defaultVoltageMax = isInDischarge ? GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.default_max : GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_max;
		const defaultVoltageMin = isInDischarge ? GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.default_min : GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_min;
		const defaultCurrentMax = isInDischarge ? GRAPH_BOUNDS.DISCHARGE_CURRENT.default_max : GRAPH_BOUNDS.FLOAT_CURRENT.default_max;
		const defaultCurrentMin = isInDischarge ? GRAPH_BOUNDS.DISCHARGE_CURRENT.default_min : GRAPH_BOUNDS.FLOAT_CURRENT.default_min;

		// Display range (initial view) uses graphScales if available, otherwise GRAPH_BOUNDS defaults - takes discharge into account
		let voltageDisplayMax = defaultVoltageMax;
		let voltageDisplayMin = defaultVoltageMin;

		if (graphScales?.voltage) {
			voltageDisplayMax = validValue(graphScales.voltage.max, defaultVoltageMax);

			const selectedVoltMin = isInDischarge ? graphScales.voltage.min_discharge : graphScales.voltage.min;
			voltageDisplayMin = validValue(selectedVoltMin, defaultVoltageMin);
		}

		let currentDisplayMax = defaultCurrentMax;
		let currentDisplayMin = defaultCurrentMin;

		if (graphScales?.current) {
			const selectedCurrentMax = isInDischarge ? graphScales.current.discharge_max : graphScales.current.float_max;
			currentDisplayMax = validValue(Math.abs(selectedCurrentMax), defaultCurrentMax);
			currentDisplayMin = defaultCurrentMin;
		}

		// Zoom/pan bounds always use GRAPH_BOUNDS limits
		const voltageBoundsMax = isInDischarge ? GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.bounds_max : GRAPH_BOUNDS.SYSTEM_VOLTAGE.bounds_max;
		const voltageBoundsMin = isInDischarge ? GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.bounds_min : GRAPH_BOUNDS.SYSTEM_VOLTAGE.bounds_min;
		const voltageMinRange = isInDischarge ? GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.min_range : GRAPH_BOUNDS.SYSTEM_VOLTAGE.min_range;
		
		const currentBoundsMax = isInDischarge ? GRAPH_BOUNDS.DISCHARGE_CURRENT.bounds_max : GRAPH_BOUNDS.FLOAT_CURRENT.bounds_max;
		const currentBoundsMin = isInDischarge ? GRAPH_BOUNDS.DISCHARGE_CURRENT.bounds_min : GRAPH_BOUNDS.FLOAT_CURRENT.bounds_min;
		const currentMinRange = isInDischarge ? GRAPH_BOUNDS.DISCHARGE_CURRENT.min_range : GRAPH_BOUNDS.FLOAT_CURRENT.min_range;

		return {
			voltageDisplayMax,
			voltageDisplayMin,
			voltageBoundsMax,
			voltageBoundsMin,
			voltageMinRange,
			currentDisplayMax,
			currentDisplayMin,
			currentBoundsMax,
			currentBoundsMin,
			currentMinRange,
		};
	}


	// Determine active graph scales based on discharge state - memoized to prevent unnecessary recalculations
	const activeSysGraphScales = useMemo(() => {
		return calculateSysStrGraphScales(eventInfo?.isDischarge, sysGraphScalesData?.system);		
	}, [eventInfo?.isDischarge, sysGraphScalesData]);

	const activeStringGraphScales = useMemo(() => {
		return calculateSysStrGraphScales(eventInfo?.isDischarge, sysGraphScalesData?.string);
	}, [eventInfo?.isDischarge, sysGraphScalesData]);

	const activeUnitGraphScaleExtremes = useMemo(() => {
		// Initial display range uses graphScales if available, otherwise GRAPH_BOUNDS defaults
		const voltDisplayMax = validValue(unitGraphScalesExtremes?.voltage?.max, GRAPH_BOUNDS.UNIT_VOLTAGE.default_max);

		const selectedVoltMin = eventInfo?.isDischarge ? unitGraphScalesExtremes?.voltage?.discharge_min : unitGraphScalesExtremes?.voltage?.min;
		const voltDisplayMin = validValue(selectedVoltMin, GRAPH_BOUNDS.UNIT_VOLTAGE.default_min);

		// Pan/zoom limits always use GRAPH_BOUNDS bounds
		const voltBoundsMax = GRAPH_BOUNDS.UNIT_VOLTAGE.bounds_max;
		const voltBoundsMin = GRAPH_BOUNDS.UNIT_VOLTAGE.bounds_min;

		return {
			voltDisplayMin,
			voltDisplayMax,
			voltBoundsMin,
			voltBoundsMax
		};
	}, [eventInfo?.isDischarge, unitGraphScalesExtremes?.voltage?.max, unitGraphScalesExtremes?.voltage?.min, unitGraphScalesExtremes?.voltage?.discharge_min]);

	// Conditionally fetch discharge limits when entering discharge state
	useEffect(() => {
		if (eventInfo?.isDischarge) {
			if (!dischargeLimitsFetchedRef.current.system && !sysDischargeLimitsData) {
				dischargeLimitsFetchedRef.current.system = true;
				Promise.resolve(UpdateSysDischargeLimitsData()).catch(() => {
					// Allow retry on next refresh if the request fails
					dischargeLimitsFetchedRef.current.system = false;
				});
			}
			if (!dischargeLimitsFetchedRef.current.string && !stringDischargeLimitsData) {
				dischargeLimitsFetchedRef.current.string = true;
				Promise.resolve(UpdateStringDischargeLimitsData()).catch(() => {
					dischargeLimitsFetchedRef.current.string = false;
				});
			}
			if (!dischargeLimitsFetchedRef.current.unit && !unitDischargeLimitsData) {
				dischargeLimitsFetchedRef.current.unit = true;
				Promise.resolve(UpdateUnitDischargeLimitsData()).catch(() => {
					dischargeLimitsFetchedRef.current.unit = false;
				});
			}
		} else {
			// Reset fetch tracking when leaving discharge state
			dischargeLimitsFetchedRef.current = {
				system: false,
				string: false,
				unit: false,
			};
		}
	}, [eventInfo?.isDischarge, sysDischargeLimitsData, stringDischargeLimitsData, unitDischargeLimitsData]);

	// Event Handlers
	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	};

	const handleViewStaleData = () => {
		setStaleDataState(prevState => ({
			...prevState,
			dialogOpen: false,
			viewingStaleData: true,
		}));
		// Keep auto-refresh disabled when viewing stale data
		setAutoUpdate(prevState => ({
			...prevState,
			isAllowed: false,
			isEnabled: false,
		}));
	};

	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'hidden',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='2px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar sx={{ minHeight: '48px !important', py: 0.5 }}>
				<Grid container spacing={0.5} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							Real-time Data
						</Typography>
					</Grid>
					<Grid item xs alignItems="left">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Typography
								align="left"
								fontFamily='Saira,sans-serif'
								fontWeight='600'
							>
								{ headingText }
							</Typography>
							{!isNarrowScreen && eventInfo && (
								<EventNotificationBar eventInfo={eventInfo} narrowScreen={false} />
							)}
						</Box>
					</Grid>
					<Grid item>
						<Tooltip title="Select string to filter on the graph" arrow>
							<Box>
								<FormControl sx={{ m: 0, minWidth: 80, height: '32px' }}>
									<select
										id="string-select"
										value={selectedString}
										label="String"
										onChange={handleStringChange}
										style={{ height: '32px', padding: '4px 8px' }}
									>
										<option key={0} value={0}>All Strings</option>
										{ Array.from({length: locationInfo.num_strings}, (_, i) => i + 1).map((stringNum) => (
											<option key={stringNum} value={stringNum}>String {stringNum}</option>
										)) }
									</select>
								</FormControl>
							</Box>
						</Tooltip>
					</Grid>
					{ autoUpdate.isAllowed && (
						<Grid item>
							<Tooltip title="Set auto-update interval for realtime data" arrow>
								<Box>
									<FormControl sx={{ m: 0, minWidth: 50, height: '32px' }}>
										<select
											id="interval-select"
											value={autoUpdate.interval}
											label="Interval"
											onChange={(event) => {
												const selectedInterval = Number(event.target.value);
												setAutoUpdate(prevState => ({
													...prevState,
													interval: selectedInterval,
													isEnabled: selectedInterval > 0
												}));
											}}
											style={{ height: '32px', padding: '4px 8px' }}
										>
											{ RealtimeIntervals.map((interval) => (
												<option key={interval.value} value={interval.value}>{interval.label}</option>
											)) }
										</select>
									</FormControl>
								</Box>
							</Tooltip>
						</Grid>
					)}
					<Grid item>
						<Tooltip title={staleDataState.viewingStaleData ? "Refresh disabled for stale data" : "Manually refresh realtime data"} arrow>
							<Box>
								<CooldownButton
									onClick={UpdateRealtimeData}
									disabled={isLoading || staleDataState.viewingStaleData}
									cooldownSeconds={10}
									icon={<RefreshIcon />}
									ariaLabel="refresh realtime data"
									color="primary"
									sx={{ padding: '4px', minWidth: '32px', height: '32px' }}
								/>
							</Box>
						</Tooltip>
					</Grid>
				</Grid>
			</Toolbar>
			{isTimerVisible && (
				<>
					<Divider />
					<Box sx={{
						px: 2,
						display: 'flex',
						alignItems: 'center',
						gap: 2
					}}>
						<TimerProgressBar
							interval={autoUpdate.interval}
							onIntervalReached={() => {
								UpdateRealtimeData();
							}}
							countDown={true}
							resetTrigger={timerResetTrigger}
						/>
					</Box>
				</>
			)}
			<Divider />
			<Box
				sx={{
					position: 'relative',
					flexGrow: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
					overflow: 'hidden',
					...(eventInfo && {
						border: `4px solid ${eventInfo.color}`,
						borderRadius: '4px',
						marginLeft: '0px',
						marginRight: '0px',
						marginTop: '4px',
						marginBottom: '2px',
					}),
				}}
			>
				{isNarrowScreen && eventInfo && (
					<Box>
						<EventNotificationBar eventInfo={eventInfo} narrowScreen={true} />
					</Box>
				)}
				<Grid
					container
					spacing={1}
					sx={{
						flexGrow: 1,
						minHeight: 0,
						pt: 1,
					}}
				>
					<Grid item xs={4} lg={3} sx={{
						height: '33%',
						backgroundColor: 'rgba(35, 141, 193, 0.1)',
						overflow: 'hidden'
					}}>
						<SystemRealtimeGraph
							locationNum={locationNum}
							systemRealtimeData={realtimeData?.system}
							limitData={activeSysLimits}
							graphScales={activeSysGraphScales}
							hasValidData={hasValidSystem}
							loading={isLoading}
						/>
					</Grid>
					<Grid item xs={8} lg={9} sx={{
						height: '33%',
						backgroundColor: 'rgba(245, 179, 53, 0.1)',
						overflow: 'hidden'
					}}>
						<StringRealtimeGraph
							locationNum={locationNum}
							stringsRealtimeData={realtimeData?.strings}
							limitData={activeStringLimits}
							graphScales={activeStringGraphScales}
							selectedString={selectedString}
							setSelectedString={setSelectedString}
							hasValidData={hasValidStrings}
							loading={isLoading}
						/>
					</Grid>
					<Grid item xs={12} sx={{
						height: '33%',
						backgroundColor: 'rgba(76, 156, 46, 0.1)',
						overflow: 'hidden'
					}}>
						<TemperatureRealtimeGraph
							locationNum={locationNum}
							realtimeData={realtimeData}
							sysLimitData={activeSysLimits?.temp}
							sysGraphScales={sysGraphScalesData?.system?.temperature}
							graphScales={unitGraphScalesExtremes?.temperature}
							selectedString={selectedString}
							hasValidTempSensorData={hasValidTempSensor}
							hasValidUnitTempData={hasValidUnitTemp}
							loading={isLoading}
						/>
					</Grid>
					<Grid item xs={12} sx={{
						height: '34%',
						backgroundColor: 'rgba(243, 39, 53, 0.1)',
						overflow: 'hidden'
					}}>
						<UnitsRealtimeGraph
							locationNum={locationNum}
							unitRealtimeData={realtimeData?.units}
							limitData={activeUnitLimits}
							graphScales={activeUnitGraphScaleExtremes}
							selectedString={selectedString}
							hasValidData={hasValidUnits}
							loading={isLoading}
						/>
					</Grid>
				</Grid>
			</Box>

			{/* Stale Data Blocker - only covers this content area */}
			<StaleDataBlocker
				staleDataState={staleDataState}
				onViewStaleData={handleViewStaleData}
			/>
		</Paper>
	);
}